<template>
    <NonSkuStoneSearch
        :get-stone-route
        :active-filter-count
        :bounds="boundsInternal"
        :category="StoneCategoryCode.LabGrownDiamond"
        :errors
        :fetching
        :filter-chips
        :get-sort-route
        :response
        :route-reset
        :shape="filters?.shape || undefined"
        :sorting
        :sorting-options
        :stale
        :stones-collection
        @sort-by-carat="sortByCarat"
        @sort-by-price="sortByPrice"
    >
        <template #before-filters>
            <CmsBlocks
                :data="data?.pageProductListing?.beforeFilters || []"
                page-type="product-listing"
            />
        </template>

        <template #filters-quick>
            <ShapeFilter
                v-if="shapes"
                v-qa="'filter-shape'"
                :get-route="getRouteShape"
                :locked-to="ringShape"
                :model-value="filters?.shape || undefined"
                :selected-shape="filters?.shape || undefined"
                :shapes
            />
        </template>

        <template #preview="{ stone, tableView }">
            <StonePreview
                :data-qa-stone-props="
                    JSON.stringify({
                        id: stone.id,
                    })
                "
                :stone
                :stone-route="getStoneRoute(stone.id)"
                :table-view
                :sorting
            />
        </template>

        <template #after-grid>
            <CmsBlocks
                :data="data?.pageProductListing?.afterGrid || []"
                page-type="product-listing"
            />
        </template>

        <template #filters-chips>
            <FilterChips
                v-qa="'filter-chips-filter'"
                :chips="filterChips"
                :route-reset
            />
        </template>

        <template #filters>
            <LabGrownDiamondFilters
                v-if="boundsInternal"
                v-model="filters"
                :bounds="boundsInternal"
            >
                <template #stone-type>
                    <StoneSearchTabs
                        :shape="
                            gemsApiShapeToShapeCode(filters?.shape || undefined)
                        "
                    />
                </template>

                <template #price>
                    <FilterPriceRangeExponential
                        v-if="boundsInternal && boundsInternal.price"
                        v-model="priceFilterModel"
                        v-qa="'filter-price'"
                        :bounds="boundsInternal.price"
                        narrow
                    />
                </template>
            </LabGrownDiamondFilters>
        </template>

        <template #no-results-found>
            <ThTitle
                alignment="CENTER"
                color="var(--blue)"
                size="SMALL"
                tag="H3"
            >
                We couldn't find any lab-grown diamonds matching your current
                search criteria.
            </ThTitle>

            <ThSpacer size="SMALL" />

            <ThText
                alignment="CENTER"
                color="var(--slate)"
                size="MEDIUM"
            >
                <p>
                    There are natural diamonds available for this shape –
                    <ThLink
                        :to="
                            routes.diamondSearch(GemsApi_StoneCategory.Diamond)
                        "
                    >
                        return to the natural diamond search.
                    </ThLink>
                </p>

                <p>
                    Please
                    <ThLink @click="openSpeakToExpertModal">contact us</ThLink>
                    if you have a specific diamond search request and we can
                    help you with your enquiry.
                </p>
            </ThText>
        </template>

        <template #stone-modal>
            <LabGrownDiamondModal
                :data-qa="'lg-modal'"
                :stone="modalStone"
                page-type="product-listing"
                @close="closeStoneModal"
                @select="selectStone"
            />
        </template>
    </NonSkuStoneSearch>
</template>

<script lang="ts" setup>
import {
    computed,
    defineAsyncComponent,
    hydrateOnVisible,
    ref,
    type Ref,
    toRef,
} from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import {
    GemsApi_StoneCategory,
    type NonSkuLabGrownDiamondBoundsFragment,
    type NonSkuLabGrownDiamondsFilters,
    type NonSkuLabGrownDiamondSortInput,
    type NonSkuLabGrownFragment,
    NonSkuLabGrownSortField,
    NonSkuSortDirection,
    StoneCategoryCode,
    type StoneShapeCode,
} from '@/graphql/api'

import { useCountryPlugin } from '@/plugins/country.ts'
import { useRoutes } from '@/router/routes'
import { useTranslator } from '@/i18n'
import {
    type DecodedLabGrownDiamondFiltersAndIdentifier,
    decodeNonSkuLabGrownDiamondFiltersAndSort,
} from '@/domain/stone/search/nonsku/lab-grown-diamond'
import { useAllowedShapes } from '@/domain/stone/search/nonsku/shape'
import { useResponseError } from '@/helpers/errors'
import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import { useRouterNavigation } from '@/plugins/released-urls/useRouterNavigation'
import { useActivated } from '@/composables/useActivated'
import { useMeta } from '@/composables/app/useMeta'
import { useFiltersCount } from '@/views/product/useFiltersCount'
import { useRingShapeConstraintForNonSKU } from '@/domain/stone/search/useOrderedShapeFragments'
import { useSpeakToExpertModal } from '@/composables/app/useSpeakToExpertModal'
import { getClearFilter } from '@/helpers/filter'
import { formatNumericProperty } from '@/domain/stone/labels'
import { useFormattedPriceRange } from '@/composables/useMoneyFormatter'

import type { DropdownOption } from '@/components/ui/SortingDropdown.vue'
import type { FilterChip } from '@/components/ui/filter/FilterChips.vue'
import FilterChips from '@/components/ui/filter/FilterChips.vue'

import NonSkuStoneSearch from '@/components/stone/search/nonsku/NonSkuStoneSearch.vue'
import ShapeFilter from '@/components/stone/search/ShapeFilter.vue'
import LabGrownDiamondModal from '@/components/stone/search/nonsku/modal/LabGrownDiamondModal.vue'
import LabGrownDiamondFilters from '@/components/stone/search/nonsku/LabGrownDiamondFilters.vue'
import FilterPriceRangeExponential from '@/components/stone/search/nonsku/filter/FilterPriceRangeExponential.vue'
import StonePreview from '@/components/stone/StonePreview.vue'
import ThLink from '@/components/ui/buttons/ThLink.vue'
import ThSpacer from '@/components/ui/ThSpacer.vue'
import ThTitle from '@/components/ui/title/ThTitle.vue'
import ThText from '@/components/ui/ThText.vue'
import { systemCodeToWords } from '@/helpers'
import { useRedirect } from 'wucdbm-vite-vue-ssr'
import {
    createObjectComputed,
    createObjectWritableComputed,
} from '@/composables/createObjectComputed.js'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import StoneSearchTabs from '@/components/stone/search/StoneSearchTabs.vue'
import { gemsApiShapeToShapeCode } from '@/domain/stone/search/sku/shape'
import { useLabGrownSearchQuery } from '@/views/stone/search/nonsku/LabGrownSearch.generated.ts'
import { useRingBuilder } from '@/plugins/ring-builder.ts'
import { useWatchRedirectImmediate } from '@/helpers/redirect.ts'

const CmsBlocks = defineAsyncComponent({
    loader: () => import('@/components/cms/CmsBlocks.vue'),
    hydrate: hydrateOnVisible(),
})

const { hash } = defineProps<{
    hash: string
}>()

const router = useRouter()
const routes = useRoutes()

const { countryCode } = useCountryPlugin()
const { locale } = useTranslator()
const currencyInput = useCurrencyInput()
const { t } = useI18n()
const redirect = useRedirect()

const { open: openSpeakToExpertModal } = useSpeakToExpertModal()

const hashParams = computed(
    (): DecodedLabGrownDiamondFiltersAndIdentifier | undefined =>
        decodeNonSkuLabGrownDiamondFiltersAndSort(hash.substring(1))
)

const stoneId = computed(() => hashParams.value?.stoneId)

const filters = createObjectWritableComputed({
    get: (): Required<NonSkuLabGrownDiamondsFilters> => ({
        priceRange: null,
        caratWeightRange: null,
        colors: null,
        shape: null,
        ...hashParams.value?.filters,
    }),
    set: (filters) => {
        router.push(
            routes.nonSku.labGrownDiamondSearch(
                filters,
                sorting.value,
                stoneId.value
            )
        )
    },
})

const priceFilterModel = computed({
    get: () => filters.value?.priceRange,
    set: (priceRange) => {
        filters.value = { ...filters.value, priceRange }
    },
})

const formattedPriceRange = useFormattedPriceRange(
    toRef(() => filters.value.priceRange)
)

const defaultSort: NonSkuLabGrownDiamondSortInput = {
    field: 'PRICE',
    direction: 'ASC',
}

const sorting = createObjectComputed(
    (): NonSkuLabGrownDiamondSortInput => hashParams.value?.sort || defaultSort
)

const sortingOptions: DropdownOption<NonSkuLabGrownDiamondSortInput>[] = [
    {
        value: {
            direction: NonSkuSortDirection.Asc,
            field: NonSkuLabGrownSortField.Price,
        },
        label: 'Price',
        directionLabel: '(low to high)',
        directionIcon: undefined,
    },
    {
        value: {
            direction: NonSkuSortDirection.Desc,
            field: NonSkuLabGrownSortField.Price,
        },
        label: 'Price',
        directionLabel: '(high to low)',
        directionIcon: undefined,
    },
    {
        value: {
            direction: NonSkuSortDirection.Asc,
            field: NonSkuLabGrownSortField.Carat,
        },
        label: 'Weight',
        directionLabel: '(low to high)',
        directionIcon: undefined,
    },
    {
        value: {
            direction: NonSkuSortDirection.Desc,
            field: NonSkuLabGrownSortField.Carat,
        },
        label: 'Weight',
        directionLabel: '(high to low)',
        directionIcon: undefined,
    },
]

const getSortRoute = (
    sorting: NonSkuLabGrownDiamondSortInput
): ButtonRouteRelative =>
    routes.nonSku.labGrownDiamondSearch(
        { ...filters.value },
        sorting,
        stoneId.value
    )

function sortByCarat(): void {
    updateSorting(NonSkuLabGrownSortField.Carat)
}

function sortByPrice(): void {
    updateSorting(NonSkuLabGrownSortField.Price)
}

function updateSorting(field: NonSkuLabGrownSortField): void {
    let direction: NonSkuSortDirection = NonSkuSortDirection.Asc

    if (field === sorting.value.field) {
        direction =
            sorting.value.direction === NonSkuSortDirection.Asc
                ? NonSkuSortDirection.Desc
                : NonSkuSortDirection.Asc
    }

    router.push(
        routes.nonSku.labGrownDiamondSearch(
            filters.value,
            {
                field,
                direction,
            },
            stoneId.value
        )
    )
}

const getRouteShape = (shape?: StoneShapeCode): ButtonRouteRelative =>
    routes.nonSku.labGrownDiamondSearch(
        {
            ...filters.value,
            shape,
        },
        hashParams.value?.sort,
        stoneId.value
    )

const boundsInternal: Ref<NonSkuLabGrownDiamondBoundsFragment | null> =
    ref(null)
const { shapes, ringShape } = useRingShapeConstraintForNonSKU(
    useAllowedShapes(() => boundsInternal.value?.shapes)
)

if (!import.meta.env.SSR) {
    useWatchRedirectImmediate(
        () => {
            return {
                ringShape: ringShape.value,
                hashParams: hashParams.value,
            }
        },
        ({ ringShape, hashParams }): void => {
            if (!ringShape) {
                return
            }

            if (ringShape.systemCode === hashParams?.filters?.shape) {
                return
            }

            const ringShapeRoute = getRouteShape(ringShape.systemCode)

            if (ringShapeRoute.name !== router.currentRoute.value.name) {
                // Suspense causes you to remain on the old page so its watchers are still active
                // But the URL/current route have moved to the next route
                // The reason we end up here in the first place is hashParams also become empty
                return
            }

            const route = router.resolve(ringShapeRoute)
            redirect(route.fullPath, 302)
        }
    )
}

const activeFilterCount = useFiltersCount(
    computed(() => [
        !!(
            filters.value?.caratWeightRange?.max ||
            filters.value?.caratWeightRange?.min
        ),
        !!(filters.value?.priceRange?.max || filters.value?.priceRange?.min),
        !ringShape.value && !!filters.value?.shape?.length,
        !!filters.value?.colors?.length,
    ])
)

const filterChips = computed(() => {
    const chips: FilterChip[] = []

    if (formattedPriceRange.value) {
        chips.push({
            label: formattedPriceRange.value,
            onRemove: getClearFilter(filters, 'priceRange'),
        })
    }

    if (!ringShape.value && filters.value?.shape) {
        chips.push({
            label:
                shapes.value?.find((s) => s.systemCode === filters.value.shape)
                    ?.translation?.name ||
                systemCodeToWords(filters.value.shape),
            getRouteRemove: getRouteShape,
        })
    }

    if (
        filters.value.caratWeightRange?.min &&
        filters.value.caratWeightRange?.max
    ) {
        chips.push({
            label: `${t('diamond_search.filter.carat_size.chip', {
                min: formatNumericProperty(
                    Number(filters.value.caratWeightRange.min)
                ),
                max: formatNumericProperty(
                    Number(filters.value.caratWeightRange.max)
                ),
            })}`,
            onRemove: getClearFilter(filters, 'caratWeightRange'),
        })
    }

    if (filters.value.colors) {
        filters.value.colors.forEach((code) => {
            const color = boundsInternal.value?.colors.find(
                (c) => c.systemCode === code
            )

            chips.push({
                label: t('diamond_search.filter.colour.chip', {
                    colour: color?.translation?.name,
                }),
                onRemove: getClearFilter(filters, 'colors', code),
            })
        })
    }

    return chips
})

const routeReset = computed(() => {
    return routes.nonSku.labGrownDiamondSearch({
        shape: ringShape.value?.systemCode,
    })
})

const response = useLabGrownSearchQuery({
    variables: computed(() => ({
        filters: filters.value || {},
        sort: sorting.value,
        currency: currencyInput.value,
        country: countryCode.value!,
        locale: locale.value,
        contentPath: '/non-sku-lab-grown-diamonds',
    })),
})

const stonesCollection = toRef(
    (): NonSkuLabGrownFragment[] | undefined =>
        data.value?.nonSkuLabGrownDiamonds
)

const errors = useResponseError(response)

const { data, fetching, stale } = response

const prefetchPromise = response.then((res) => {
    boundsInternal.value = res.data.value?.nonSkuLabGrownDiamondsBounds || null

    return res.data
})

useMeta(
    {
        title: () => data.value?.pageProductListing?.meta?.title || '',
        description: () =>
            data.value?.pageProductListing?.meta?.description || '',
    },
    data,
    fetching,
    stale
)

const getStoneRoute = (stoneId: string) =>
    routes.nonSku.labGrownDiamondSearch(
        filters.value,
        hashParams.value?.sort,
        stoneId
    )

const { isActivated } = useActivated()

const closeStoneModal = () => {
    if (!isActivated.value) {
        return
    }

    router.replace(
        routes.nonSku.labGrownDiamondSearch(
            filters.value,
            hashParams.value?.sort
        )
    )
}

const modalStone = computed((): NonSkuLabGrownFragment | undefined =>
    stonesCollection.value?.find((s) => s.id === stoneId.value)
)

const { routerPush } = useRouterNavigation()

const { ring } = useRingBuilder()
const selectStone = (v: NonSkuLabGrownFragment) => {
    if (ring.value) {
        routerPush(routes.ringBuilder.ring(ring.value, v))
    } else {
        routerPush(
            routes.engagementRings({
                shape: v.shape.systemCode,
            })
        )
    }
}

await prefetchPromise
</script>
